import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    BookOpen, FileText, Users, GraduationCap, Calculator,
    Clock, TrendingUp, CheckCircle2, AlertCircle, ArrowRight,
    Plus, Building2, Shield, KeyRound, Receipt, CreditCard, ClipboardList, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { formatDueDate, parseDueDate } from '../utils/assignmentDates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
    const { user } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [courses, setCourses] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [pilotReadiness, setPilotReadiness] = useState(null);
    const [accountingActivity, setAccountingActivity] = useState(null);
    const [instructorReviewQueues, setInstructorReviewQueues] = useState({ arAp: null, jv: null, load_error: false });
    const [loading, setLoading] = useState(true);

    // Admin-specific state
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminSchools, setAdminSchools] = useState([]);

    const isAdmin = user.role === 'admin';
    const isInstructor = user.role === 'instructor' || user.role === 'admin';

    const fetchDashboardData = useCallback(async () => {
        try {
            if (isAdmin) {
                // Admin: fetch platform-wide stats
                const [usersRes, schoolsRes, activityRes] = await Promise.all([
                    axios.get(`${API}/admin/users`),
                    axios.get(`${API}/admin/schools`),
                    axios.get(`${API}/controls/accounting-activity?limit=8`).catch(error => {
                        console.error('Error fetching accounting activity:', error);
                        return { data: { load_error: true, summary: {}, items: [] } };
                    }),
                ]);
                setAdminUsers(usersRes.data);
                setAdminSchools(schoolsRes.data);
                setAccountingActivity(activityRes.data);
            } else {
                // Instructor/Student: existing flow
                const requests = [
                    axios.get(`${API}/analytics/${isInstructor ? 'instructor' : 'student'}`),
                    axios.get(`${API}/courses`),
                    axios.get(`${API}/assignments`)
                ];
                if (isInstructor) {
                    requests.push(
                        axios.get(`${API}/pilot/instructor-readiness`).catch(error => {
                            console.error('Error fetching pilot readiness:', error);
                            return {
                                data: {
                                    load_error: true,
                                    totals: {},
                                    courses: [],
                                    pending_reviews: [],
                                    student_progress: []
                                }
                            };
                        }),
                        axios.get(`${API}/controls/accounting-activity?limit=8`).catch(error => {
                            console.error('Error fetching accounting activity:', error);
                            return { data: { load_error: true, summary: {}, items: [] } };
                        }),
                        axios.get(`${API}/controls/ar-ap-review-queue`).catch(error => {
                            console.error('Error fetching AR/AP review queue:', error);
                            return { data: { load_error: true, summary: { pending: 0, ar: 0, ap: 0 }, items: [] } };
                        }),
                        axios.get(`${API}/journal-vouchers/review-queue`).catch(error => {
                            console.error('Error fetching JV review queue:', error);
                            return { data: { load_error: true, items: [] } };
                        })
                    );
                }

                const [analyticsRes, coursesRes, assignmentsRes, readinessRes, activityRes, arApQueueRes, jvQueueRes] = await Promise.all(requests);
                setAnalytics(analyticsRes.data);
                setCourses(coursesRes.data.slice(0, 4));
                setPilotReadiness(isInstructor ? readinessRes?.data : null);
                setAccountingActivity(isInstructor ? activityRes?.data : null);
                setInstructorReviewQueues(isInstructor ? {
                    arAp: arApQueueRes?.data || { summary: { pending: 0, ar: 0, ap: 0 }, items: [] },
                    jv: Array.isArray(jvQueueRes?.data) ? jvQueueRes.data : (jvQueueRes?.data?.items || []),
                    load_error: !!(arApQueueRes?.data?.load_error || jvQueueRes?.data?.load_error),
                } : { arAp: null, jv: null, load_error: false });

                let filteredAssignments = assignmentsRes.data;
                if (!isInstructor) {
                    try {
                        const subsRes = await axios.get(`${API}/submissions/my`);
                        const submittedIds = new Set(subsRes.data.map(s => s.assignment_id));
                        const now = new Date();
                        filteredAssignments = filteredAssignments.filter(a => {
                            if (submittedIds.has(a.id)) return false;
                            const dueDate = parseDueDate(a.due_date);
                            if (!dueDate) return false;
                            if (dueDate < now) return false;
                            return true;
                        });
                    } catch {}
                }
                setAssignments(filteredAssignments.slice(0, 5));
            }
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [isAdmin, isInstructor]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-48 skeleton" />
                <div className="dashboard-grid">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="h-32 skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    // ── Admin Dashboard ──
    if (isAdmin) {
        const tempPwCount = adminUsers.filter(u => u.must_change_password).length;
        const recentUsers = [...adminUsers].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5);

        return (
            <div className="space-y-8" data-testid="dashboard">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user.full_name.split(' ')[0]}
                        </h1>
                        <p className="text-slate-600 mt-1">Platform overview for administrators</p>
                    </div>
                    <Link to="/admin">
                        <Button className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full" data-testid="open-admin-btn">
                            <Shield className="w-4 h-4 mr-2" /> Open Admin Console <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>

                {/* Platform Stats */}
                <div className="dashboard-grid">
                    <StatCard icon={Building2} label="Total Schools" value={adminSchools.length} color="bg-[#0F2A44]" />
                    <StatCard icon={Users} label="Total Users" value={adminUsers.length} color="bg-[#2F8FCB]" />
                    <StatCard icon={BookOpen} label="Total Courses" value={adminSchools.reduce((s, sc) => s + (sc.user_count || 0), 0) > 0 ? adminSchools.length : '—'} color="bg-emerald-500" />
                    <StatCard icon={KeyRound} label="Pending Temp PW" value={tempPwCount} color={tempPwCount > 0 ? 'bg-amber-500' : 'bg-slate-400'} />
                </div>

                <InstructorAccountingActivityHub data={accountingActivity} />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Recent Users */}
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900">Recent Users</h2>
                            <Link to="/admin" className="text-sm font-medium text-[#2F8FCB] hover:text-[#0F2A44] flex items-center gap-1">
                                View all <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="space-y-3">
                            {recentUsers.map(u => (
                                <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                        u.role === 'admin' ? 'bg-red-500' : u.role === 'instructor' ? 'bg-blue-500' : 'bg-emerald-500'
                                    }`}>{u.full_name?.charAt(0).toUpperCase()}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{u.full_name}</p>
                                        <p className="text-xs text-slate-500">{u.email}</p>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                        u.role === 'admin' ? 'bg-red-100 text-red-700' : u.role === 'instructor' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                                    }`}>{u.role}</span>
                                    {u.must_change_password && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded">Temp PW</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Schools Quick List */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900 mb-4">Schools</h2>
                        {adminSchools.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">No schools created yet</p>
                        ) : (
                            <div className="space-y-3">
                                {adminSchools.map(s => (
                                    <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-[#2F8FCB]" />
                                            <span className="text-sm font-medium text-slate-900">{s.name}</span>
                                        </div>
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{s.user_count} users</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Auditor Dashboard ──
    if (user.role === 'auditor') {
        return (
            <div className="space-y-8" data-testid="dashboard">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                        Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user.full_name.split(' ')[0]}
                    </h1>
                    <p className="text-slate-600 mt-1">Auditor — Read-only access to the Accounting Workspace</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                    <p className="text-amber-800 text-sm">You have read-only access to the Accounting Workspace. Contact your administrator to request changes.</p>
                </div>
                <Link to="/workspace">
                    <Button className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full" data-testid="workspace-cta-btn">
                        <Calculator className="w-4 h-4 mr-2" /> Open Accounting Workspace <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="dashboard">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                        Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user.full_name.split(' ')[0]}
                    </h1>
                    <p className="text-slate-600 mt-1">
                        {isInstructor 
                            ? 'Here\'s what\'s happening with your courses today'
                            : 'Track your progress and stay on top of your assignments'
                        }
                    </p>
                </div>
                {isInstructor && (
                    <Link to="/courses">
                        <Button className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full" data-testid="create-course-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            New Course
                        </Button>
                    </Link>
                )}
            </div>

            {/* Stats Grid */}
            <div className="dashboard-grid">
                {isInstructor ? (
                    <>
                        <StatCard
                            icon={BookOpen}
                            label="Total Courses"
                            value={analytics?.total_courses || 0}
                            color="bg-[#0F2A44]"
                        />
                        <StatCard
                            icon={Users}
                            label="Total Students"
                            value={analytics?.total_students || 0}
                            color="bg-[#2F8FCB]"
                        />
                        <StatCard
                            icon={CheckCircle2}
                            label="Graded"
                            value={analytics?.submissions_graded || 0}
                            color="bg-emerald-500"
                        />
                        <StatCard
                            icon={AlertCircle}
                            label="LMS Pending Review"
                            value={analytics?.submissions_pending || 0}
                            color="bg-amber-500"
                            testId="stat-pending-review"
                        />
                    </>
                ) : (
                    <>
                        <StatCard
                            icon={BookOpen}
                            label="Enrolled Courses"
                            value={analytics?.enrolled_courses || 0}
                            color="bg-[#0F2A44]"
                        />
                        <StatCard
                            icon={FileText}
                            label="Assignments"
                            value={analytics?.total_assignments || 0}
                            color="bg-[#2F8FCB]"
                        />
                        <StatCard
                            icon={CheckCircle2}
                            label="Submitted"
                            value={analytics?.submitted || 0}
                            color="bg-emerald-500"
                        />
                        <StatCard
                            icon={TrendingUp}
                            label="Avg. Grade"
                            value={`${analytics?.average_grade || 0}%`}
                            color="bg-violet-500"
                        />
                    </>
                )}
            </div>

            {isInstructor && (
                <InstructorActionCenter
                    readiness={pilotReadiness}
                    reviewQueues={instructorReviewQueues}
                />
            )}
            {isInstructor && <InstructorAccountingActivityHub data={accountingActivity} />}
            {isInstructor && <InstructorPilotReadiness data={pilotReadiness} />}

            {/* Bento Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Courses Section */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900">
                            {isInstructor ? 'Your Courses' : 'Enrolled Courses'}
                        </h2>
                        <Link to="/courses" className="text-sm font-medium text-[#2F8FCB] hover:text-[#0F2A44] flex items-center gap-1">
                            View all <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {courses.length === 0 ? (
                        <div className="text-center py-12">
                            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500">
                                {isInstructor ? 'No courses created yet' : 'Not enrolled in any courses'}
                            </p>
                            <Link to="/courses">
                                <Button variant="outline" className="mt-4 rounded-full">
                                    {isInstructor ? 'Create Course' : 'Browse Courses'}
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {courses.map((course, index) => (
                                <Link
                                    key={course.id}
                                    to={`/courses/${course.id}`}
                                    className={`course-card p-4 animate-fade-in stagger-${index + 1}`}
                                    data-testid={`course-card-${course.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                                            <BookOpen className="w-5 h-5 text-[#2F8FCB]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-slate-900 truncate">{course.title}</h3>
                                            <p className="text-sm text-slate-500">{course.code}</p>
                                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                                                <Users className="w-3 h-3" />
                                                <span>{course.enrolled_count} students</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* Assignments Section */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900">
                            {isInstructor ? 'Recent Assignments' : 'Upcoming Deadlines'}
                        </h2>
                        <Link to="/assignments" className="text-sm font-medium text-[#2F8FCB] hover:text-[#0F2A44]">
                            View all
                        </Link>
                    </div>

                    {assignments.length === 0 ? (
                        <div className="text-center py-8">
                            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">No assignments yet</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {assignments.map((assignment, index) => {
                                const dueDate = parseDueDate(assignment.due_date);
                                const isOverdue = dueDate ? dueDate < new Date() : false;
                                const isDueSoon = !isOverdue && (dueDate - new Date()) < 3 * 24 * 60 * 60 * 1000;

                                return (
                                    <div
                                        key={assignment.id}
                                        className={`p-3 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors animate-fade-in stagger-${index + 1}`}
                                        data-testid={`assignment-item-${assignment.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-2 h-2 rounded-full mt-2 ${
                                                isOverdue ? 'bg-red-500' : isDueSoon ? 'bg-amber-500' : 'bg-emerald-500'
                                            }`} />
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium text-slate-900 text-sm truncate">{assignment.title}</h4>
                                                <p className="text-xs text-slate-500 truncate">{assignment.course_title}</p>
                                                <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{formatDueDate(assignment.due_date)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Student: Accounting Workspace CTA */}
            {!isInstructor && (
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] rounded-2xl p-6 md:p-8 text-white">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                                <Calculator className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold font-['Manrope']">Accounting Workspace</h3>
                                <p className="text-white/70 mt-1">
                                    Practice journal entries, build your ledger, and generate financial statements
                                </p>
                            </div>
                        </div>
                        <Link to="/workspace">
                            <Button className="bg-white text-[#0F2A44] hover:bg-slate-100 rounded-full" data-testid="workspace-cta-btn">
                                Open Workspace
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
    <div className="stat-card" data-testid={testId || `stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="flex items-center justify-between">
            <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
        <div className="mt-4">
            <p className="text-3xl font-bold font-['Manrope'] text-slate-900">{value}</p>
            <p className="text-sm text-slate-500 mt-1">{label}</p>
        </div>
    </div>
);

const readinessBadge = {
    ready: 'bg-emerald-100 text-emerald-700',
    needs_review: 'bg-amber-100 text-amber-700',
    returned_work: 'bg-violet-100 text-violet-700',
    in_progress: 'bg-blue-100 text-blue-700',
    setup_needed: 'bg-rose-100 text-rose-700',
};

const readinessLabel = {
    ready: 'Ready',
    needs_review: 'Review',
    returned_work: 'Returned',
    in_progress: 'In progress',
    setup_needed: 'Setup',
};

const activityBadge = {
    posted: 'bg-emerald-100 text-emerald-700',
    submitted: 'bg-blue-100 text-blue-700',
    saved: 'bg-amber-100 text-amber-700',
    draft: 'bg-amber-100 text-amber-700',
    reversed: 'bg-purple-100 text-purple-700',
    cancelled: 'bg-rose-100 text-rose-700',
};

const activityIcon = {
    ar: Receipt,
    ap: CreditCard,
    jv: ClipboardList,
    gl: Banknote,
};

const formatActivityAmount = (value) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(value || 0));

const formatActivityDate = (value) => {
    if (!value) return 'No date';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
};

const activitySourceTypeByTab = {
    arinvoices: 'ar_invoice',
    arcollections: 'ar_collection',
    apvouchers: 'ap_voucher',
    appayments: 'ap_payment',
    jv: 'jv',
};

const buildActivityWorkspacePath = (row = {}) => {
    if (!row.workspace_path) return '';
    const [basePath, queryString = ''] = row.workspace_path.split('?');
    const params = new URLSearchParams(queryString);
    const sourceType = row.source_type || activitySourceTypeByTab[row.workspace_tab];

    if (row.course_code) params.set('course_code', row.course_code);
    if (row.course_title) params.set('course_title', row.course_title);

    if (sourceType && (row.record_id || row.record_number)) {
        params.set('open_source', '1');
        params.set('source_origin', 'Dashboard activity');
        params.set('source_type', sourceType);
        if (row.record_id) params.set('source_id', row.record_id);
        if (row.record_number) params.set('ref_no', row.record_number);
        if (row.record_type) params.set('source_label', row.record_type);
    }

    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
};

const buildWorkspaceQueuePath = (tab, item = {}) => {
    const params = new URLSearchParams({ tab });
    if (item.course_id) params.set('course_id', item.course_id);
    if (item.course_code) params.set('course_code', item.course_code);
    if (item.course_title) params.set('course_title', item.course_title);
    const query = params.toString();
    return `/workspace${query ? `?${query}` : ''}`;
};

const courseLabelFor = (item = {}) => (
    [item.course_code, item.course_title].filter(Boolean).join(' - ') || 'No course context'
);

const buildInstructorActionItems = (readiness, reviewQueues) => {
    const lmsItems = (readiness?.pending_reviews || []).map(item => ({
        id: `lms-${item.submission_id || item.assignment_id}`,
        domain: 'LMS',
        domainClass: 'bg-amber-100 text-amber-700',
        icon: FileText,
        title: item.assignment_title || 'Assignment submission',
        reference: item.submission_id ? `Submission ${item.submission_id}` : 'Submitted assignment',
        studentName: item.student_name || 'Student',
        courseLabel: courseLabelFor(item),
        submittedAt: item.submitted_at,
        statusLabel: item.is_late ? 'Late submission' : 'Submitted',
        statusClass: item.is_late ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700',
        nextAction: 'Review the answer, grade it, or return it with feedback.',
        actionLabel: 'Review / Grade',
        path: item.assignment_id ? `/assignments/${item.assignment_id}` : '/assignments',
        sortAt: item.submitted_at || '',
    }));

    const arApItems = (reviewQueues?.arAp?.items || []).map(item => {
        const Icon = activityIcon[item.module] || Calculator;
        return {
            id: `arap-${item.id || item.record_id}`,
            domain: 'Accounting',
            domainClass: 'bg-blue-100 text-blue-700',
            icon: Icon,
            title: `${item.record_type || 'Accounting source'} ${item.record_number || ''}`.trim(),
            reference: item.record_number || item.source_reference || item.record_id || 'Submitted source',
            studentName: item.student_name || 'Student',
            courseLabel: courseLabelFor(item),
            submittedAt: item.submitted_at || item.event_at || item.record_date,
            statusLabel: item.status_label || 'Submitted for Review',
            statusClass: 'bg-blue-100 text-blue-700',
            nextAction: item.next_safe_action || 'Open the AR/AP queue, review source evidence, then approve/post or return.',
            impact: item.downstream_impact || item.gl_impact || 'No GL or Trial Balance impact until posted.',
            actionLabel: 'Open AR/AP Queue',
            path: buildWorkspaceQueuePath('arapreview', item),
            sortAt: item.submitted_at || item.event_at || item.record_date || '',
        };
    });

    const jvItems = (reviewQueues?.jv || []).map(item => ({
        id: `jv-${item.id || item.jv_number}`,
        domain: 'Accounting',
        domainClass: 'bg-blue-100 text-blue-700',
        icon: ClipboardList,
        title: `Journal Voucher ${item.jv_number || ''}`.trim(),
        reference: item.jv_number || item.id || 'Submitted JV',
        studentName: item.student_name || 'Student',
        courseLabel: courseLabelFor(item),
        submittedAt: item.submitted_at || item.date,
        statusLabel: 'Submitted for Review',
        statusClass: 'bg-blue-100 text-blue-700',
        nextAction: 'Review the JV, then approve/post or return with feedback.',
        impact: 'No GL or Trial Balance impact until approved and posted.',
        actionLabel: 'Open JV Queue',
        path: buildWorkspaceQueuePath('jv', item),
        sortAt: item.submitted_at || item.date || '',
    }));

    return [...lmsItems, ...arApItems, ...jvItems].sort((a, b) => (b.sortAt || '').localeCompare(a.sortAt || ''));
};

const InstructorActionCenter = ({ readiness, reviewQueues }) => {
    if (!readiness && !reviewQueues?.arAp && !reviewQueues?.jv) {
        return (
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="instructor-action-center">
                <div className="h-6 w-64 skeleton mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-16 skeleton rounded-xl" />)}
                </div>
            </section>
        );
    }

    const actionItems = buildInstructorActionItems(readiness, reviewQueues);
    const lmsCount = readiness?.totals?.pending_reviews ?? (readiness?.pending_reviews || []).length;
    const accountingCount = (reviewQueues?.arAp?.summary?.pending || 0) + (reviewQueues?.jv || []).length;
    const totalCount = lmsCount + accountingCount;

    return (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="instructor-action-center">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900">Instructor Action Center</h2>
                    <p className="text-sm text-slate-500">
                        LMS submissions and accounting approvals that need instructor attention.
                    </p>
                    {(readiness?.load_error || reviewQueues?.load_error) && (
                        <p className="text-xs text-amber-600 mt-1">
                            Some review details are temporarily unavailable. Direct review links still work.
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/gradebook">
                        <Button variant="outline" className="rounded-full" data-testid="action-center-gradebook-btn">
                            Gradebook <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/workspace?tab=arapreview">
                        <Button variant="outline" className="rounded-full" data-testid="action-center-arap-btn">
                            AR/AP Queue <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/workspace?tab=jv">
                        <Button variant="outline" className="rounded-full" data-testid="action-center-jv-btn">
                            JV Queue <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <MiniMetric label="Needs My Review" value={totalCount} testId="action-center-total-count" />
                <MiniMetric label="LMS Submissions" value={lmsCount} testId="action-center-lms-count" />
                <MiniMetric label="Accounting Approvals" value={accountingCount} testId="action-center-accounting-count" />
            </div>

            {actionItems.length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                    <p className="font-medium text-slate-900">No pending student work needs review.</p>
                    <p className="text-sm text-slate-500 mt-1">
                        New assignment submissions, submitted JVs, and submitted AR/AP source documents will appear here.
                    </p>
                </div>
            ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="hidden md:grid grid-cols-[1.3fr_1.1fr_1.2fr_1fr_auto] gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase text-slate-500">
                        <span>Work Item</span>
                        <span>Student</span>
                        <span>Course</span>
                        <span>Submitted</span>
                        <span>Action</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {actionItems.slice(0, 10).map(item => {
                            const Icon = item.icon || FileText;
                            return (
                                <div
                                    key={item.id}
                                    className="grid grid-cols-1 md:grid-cols-[1.3fr_1.1fr_1.2fr_1fr_auto] gap-3 px-4 py-4 items-center"
                                    data-testid={`action-center-item-${item.id}`}
                                >
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                            <Icon className="w-4 h-4 text-[#0F2A44]" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${item.domainClass}`}>
                                                    {item.domain}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${item.statusClass}`}>
                                                    {item.statusLabel}
                                                </span>
                                            </div>
                                            <p className="font-semibold text-sm text-slate-900 truncate mt-1">{item.title}</p>
                                            <p className="text-xs text-slate-500 truncate">{item.reference}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm font-medium text-slate-900 truncate">{item.studentName}</p>
                                    <div className="min-w-0">
                                        <p className="text-sm text-slate-700 truncate">{item.courseLabel}</p>
                                        <p className="text-xs text-slate-500 truncate">{item.nextAction}</p>
                                        {item.impact && <p className="text-xs text-blue-700 truncate">{item.impact}</p>}
                                    </div>
                                    <p className="text-sm text-slate-600">{formatActivityDate(item.submittedAt)}</p>
                                    <Link to={item.path}>
                                        <Button variant="outline" size="sm" className="rounded-full" data-testid={`action-center-open-${item.id}`}>
                                            {item.actionLabel} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                    {actionItems.length > 10 && (
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
                            Showing the 10 newest pending items. Use Gradebook, AR/AP Queue, or JV Queue to review the full list.
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

const InstructorAccountingActivityHub = ({ data }) => {
    if (!data) {
        return (
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="accounting-activity-hub">
                <div className="h-6 w-64 skeleton mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-16 skeleton rounded-xl" />)}
                </div>
            </section>
        );
    }

    const summary = data.summary || {};
    const rows = data.items || [];

    return (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="accounting-activity-hub">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900">Recent Student Accounting Activity</h2>
                    <p className="text-sm text-slate-500">
                        Student action, status, review path, and GL/Trial Balance impact in one place.
                    </p>
                    {data.load_error && (
                        <p className="text-xs text-amber-600 mt-1">Activity is temporarily unavailable. Workspace links still work.</p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/workspace?tab=arapreview">
                        <Button variant="outline" className="rounded-full border-blue-200 text-[#0F2A44]" data-testid="activity-open-arap-review-btn">
                            AR/AP Queue <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/workspace">
                        <Button variant="outline" className="rounded-full" data-testid="activity-open-workspace-btn">
                            Open Workspace <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <MiniMetric label="Recent Records" value={summary.total || 0} />
                <MiniMetric label="AR/AP Queue" value={summary.ar_ap_queue || 0} />
                <MiniMetric label="Accounting Needs Review" value={summary.needs_review || 0} />
                <MiniMetric label="Posted" value={summary.posted || 0} />
                <MiniMetric label="Affects GL/TB" value={summary.affects_gl || 0} />
            </div>

            {rows.length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
                    No recent student accounting records yet. When a student creates an invoice, JV, AP voucher, payment, or posted GL entry, it will appear here.
                </div>
            ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="hidden md:grid grid-cols-[1.4fr_1.1fr_1fr_1fr_1.2fr_auto] gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase text-slate-500">
                        <span>Record</span>
                        <span>Student</span>
                        <span>Date</span>
                        <span className="text-right">Amount</span>
                        <span>Status / Next</span>
                        <span>Action</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {rows.map(row => {
                            const Icon = activityIcon[row.module] || Calculator;
                            const activityWorkspacePath = buildActivityWorkspacePath(row);
                            return (
                                <div
                                    key={row.id}
                                    className="grid grid-cols-1 md:grid-cols-[1.4fr_1.1fr_1fr_1fr_1.2fr_auto] gap-3 px-4 py-4 items-center"
                                    data-testid={`accounting-activity-${row.id}`}
                                >
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                            <Icon className="w-4 h-4 text-[#0F2A44]" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-sm text-slate-900 truncate">
                                                {row.record_type} {row.record_number || ''}
                                            </p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {row.counterparty_label}: {row.counterparty_name || 'N/A'}
                                            </p>
                                            <p className="text-xs text-slate-400 truncate">{row.module_label}</p>
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{row.student_name}</p>
                                        <p className="text-xs text-slate-500 truncate">{row.course_code || row.course_title || 'No course context'}</p>
                                    </div>
                                    <p className="text-sm text-slate-600">{formatActivityDate(row.event_at || row.record_date)}</p>
                                    <p className="text-sm font-mono text-slate-900 md:text-right">{formatActivityAmount(row.amount)}</p>
                                    <div>
                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${activityBadge[row.status] || 'bg-slate-100 text-slate-700'}`}>
                                            {row.status_label || row.status}
                                        </span>
                                        <p className="text-xs text-slate-500 mt-1">{row.downstream_impact}</p>
                                        {row.next_safe_action && (
                                            <p className="text-[11px] leading-4 text-slate-500 mt-1">
                                                <span className="font-semibold text-slate-600">Next:</span> {row.next_safe_action}
                                            </p>
                                        )}
                                    </div>
                                    {activityWorkspacePath ? (
                                        <Link to={activityWorkspacePath}>
                                            <Button variant="outline" size="sm" className="rounded-full" data-testid={`open-activity-${row.id}`}>
                                                Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                                            </Button>
                                        </Link>
                                    ) : (
                                        <Button variant="outline" size="sm" className="rounded-full" disabled>Open</Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
};

const InstructorPilotReadiness = ({ data }) => {
    if (!data) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="instructor-pilot-readiness">
                <div className="h-6 w-48 skeleton mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-20 skeleton rounded-xl" />)}
                </div>
            </div>
        );
    }

    const totals = data.totals || {};
    const pending = data.pending_reviews || [];
    const courseRows = data.courses || [];
    const progressRows = data.student_progress || [];

    return (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="instructor-pilot-readiness">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900">Pilot Readiness</h2>
                    <p className="text-sm text-slate-500">Course setup, submissions, and student progress needing instructor attention</p>
                    {data.load_error && (
                        <p className="text-xs text-amber-600 mt-1">
                            Readiness details are temporarily unavailable. Course and gradebook links still work.
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/courses">
                        <Button variant="outline" className="rounded-full" data-testid="pilot-open-courses-btn">
                            Courses <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/gradebook">
                        <Button variant="outline" className="rounded-full" data-testid="pilot-open-gradebook-btn">
                            Gradebook <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <MiniMetric label="Ready Courses" value={`${totals.ready_courses || 0}/${totals.courses || 0}`} />
                <MiniMetric label="LMS Pending Reviews" value={totals.pending_reviews || 0} />
                <MiniMetric label="Missing Submissions" value={totals.missing_submissions || 0} />
                <MiniMetric label="Setup Needed" value={totals.setup_needed || 0} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="xl:col-span-2 border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-900">Course Readiness</h3>
                    </div>
                    {courseRows.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500">No assigned courses yet.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {courseRows.slice(0, 5).map(course => (
                                <div key={course.course_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`pilot-course-${course.course_id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-slate-900 truncate">{course.course_code} - {course.course_title}</p>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${readinessBadge[course.readiness_status] || 'bg-slate-100 text-slate-700'}`}>
                                                {readinessLabel[course.readiness_status] || course.readiness_status}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {course.students} students | {course.lessons} lessons | {course.assignments} assignments | {course.pending_reviews} pending review
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">{course.next_action}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Link to={`/courses/${course.course_id}?tab=students`}>
                                            <Button variant="outline" size="sm" data-testid={`pilot-course-roster-${course.course_id}`}>Roster</Button>
                                        </Link>
                                        <Link to={`/courses/${course.course_id}?tab=assignments`}>
                                            <Button variant="outline" size="sm" data-testid={`pilot-course-assignments-${course.course_id}`}>Assignments</Button>
                                        </Link>
                                        <Link to={`/workspace?course_id=${course.course_id}`}>
                                            <Button variant="outline" size="sm" data-testid={`pilot-course-workspace-${course.course_id}`}>Workspace</Button>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-900">LMS Needs Review</h3>
                    </div>
                    {pending.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500">No pending submissions.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {pending.slice(0, 5).map(item => (
                                <Link
                                    key={item.submission_id}
                                    to={`/assignments/${item.assignment_id}`}
                                    className="block p-4 hover:bg-slate-50"
                                    data-testid={`pilot-review-${item.submission_id}`}
                                >
                                    <p className="font-medium text-sm text-slate-900 truncate">{item.assignment_title}</p>
                                    <p className="text-xs text-slate-500 truncate">{item.student_name} | {item.course_code}</p>
                                    <p className={`text-xs mt-1 ${item.is_late ? 'text-rose-600' : 'text-slate-400'}`}>
                                        {item.is_late ? 'Late submission' : 'Submitted'} {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString() : ''}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {progressRows.length > 0 && (
                <div className="mt-5 border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-900">Student Progress Watchlist</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                        {progressRows.slice(0, 3).map(row => (
                            <div key={`${row.course_id}-${row.student_id}`} className="p-4" data-testid={`pilot-student-${row.student_id}-${row.course_id}`}>
                                <p className="font-medium text-slate-900 truncate">{row.student_name}</p>
                                <p className="text-xs text-slate-500">{row.course_code} | {row.submitted}/{row.total_assignments} submitted</p>
                                <div className="mt-3 flex items-center gap-2 text-xs">
                                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">{row.pending} pending</span>
                                    <span className="px-2 py-0.5 rounded bg-violet-100 text-violet-700">{row.returned} returned</span>
                                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                        {row.average_grade === null ? 'No grade' : `${row.average_grade}% avg`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

const MiniMetric = ({ label, value, testId }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={testId || `mini-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <p className="text-2xl font-bold font-['Manrope'] text-slate-900">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
);
